import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { snakeCase } from 'lodash';
import {
  Box,
  Notification,
  NotificationType,
  PagePanel,
  Table,
  WarningIcon
} from 'edenred-ui';

import { useGetMassUploadErrorsQuery } from '@epi-api/massUploadHistory/massUploadHistoryApi';
import { routerPaths } from '@epi-constants/routerPaths';
import { goToPage } from '@epi-actions/navigation';
import { Pagination } from '@epi-shared/components';

import {
  StyledTableCell,
  ErrorLine,
  ErrorValue
} from './MassUploadErrorsStyles';

const pageSize = 9;

export const MassUploadErrors = () => {
  const { t } = useTranslation();
  const { warningBackgroundColor, warningColor } = useTheme();
  const dispatch = useDispatch();
  const { requestId } = useParams<{ requestId: string }>();
  const parsedRequestId = Number.parseInt(requestId, 10);
  const [currentPage, setCurrentPage] = useState(1);
  const { data } = useGetMassUploadErrorsQuery({
    requestId: parsedRequestId,
    page: currentPage,
    pageSize
  });

  const errors = data?.items.items ?? [];
  const totalCount = data?.items.totalCount ?? 0;
  const totalPages = data?.items.totalPages ?? 0;
  const sortedErrors = useMemo(
    () => [...errors].sort((a, b) => a.rowNumber - b.rowNumber),
    [errors]
  );

  const footer = useMemo(() => {
    return {
      submitText: t('containers.beneficiaries.import_employees.errors.submit'),
      onSubmit: () => {
        dispatch(goToPage(routerPaths.importEmployees));
      },
      submitButtonWidth: '225px',
      backText: t('containers.beneficiaries.import_employees.errors.back'),
      onBack: () => {
        dispatch(goToPage(routerPaths.beneficiaries));
      }
    };
  }, []);

  return (
    <PagePanel
      title={t('containers.beneficiaries.import_employees.errors.title')}
      footer={footer}
    >
      <Box pb={2}>
        <Notification
          type={NotificationType.Error}
          text={
            <Trans
              i18nKey={
                'containers.beneficiaries.import_employees.errors.notification'
              }
              components={{
                value: <ErrorValue />
              }}
              values={{
                errorsCount: totalCount
              }}
            />
          }
        />
      </Box>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Cell>
              {t(
                'containers.beneficiaries.import_employees.errors.table.row_number'
              )}
            </Table.Cell>
            <Table.Cell>
              {t('containers.beneficiaries.import_employees.errors.table.name')}
            </Table.Cell>
            <Table.Cell>
              {t(
                'containers.beneficiaries.import_employees.errors.table.email_address'
              )}
            </Table.Cell>
            <Table.Cell>
              {t(
                'containers.beneficiaries.import_employees.errors.table.error_description'
              )}
            </Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {sortedErrors.map(error => (
            <Table.Row key={error.rowNumber}>
              <StyledTableCell backgroundColor={warningBackgroundColor}>
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon height={16} color={warningColor} />
                  {error.rowNumber}
                </Box>
              </StyledTableCell>
              <StyledTableCell>{`${error.firstName} ${error.lastName}`}</StyledTableCell>
              <StyledTableCell>{error.emailAddress}</StyledTableCell>
              <StyledTableCell>
                {error.errors.map(error => (
                  <ErrorLine key={error.fieldId}>
                    {t(
                      `containers.beneficiaries.import_employees.errors.${snakeCase(
                        error.fieldId
                      )}.${error.errorCode}`
                    )}
                  </ErrorLine>
                ))}
              </StyledTableCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {totalPages > 1 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            page={currentPage}
            count={totalPages}
            defaultRowsPerPage={pageSize}
            onChangePage={(pageNumber: number) => {
              setCurrentPage(pageNumber);
              window.scrollTo(0, 0);
            }}
          />
        </Box>
      )}
    </PagePanel>
  );
};
